// ────────────────────────────────────────────────────────────────────
// The file room's small parts: the icons, rows, checkboxes and notes that
// several of its panes each need a copy of.
//
// Split out of IntakeScreen so that the screen file holds the screen. Nothing
// here knows about intake specifically — they take props and render.
// ────────────────────────────────────────────────────────────────────

import { ChevronDown, ChevronRight, ArrowUpDown, Check, Minus,
         Image as ImageIcon, FileText, Sheet, Ruler, File as FileIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { IntakeKind } from "@/lib/types";
import type { SortKey } from "@/lib/intakeFiles";

export function KindIcon({ kind, size = 15 }: { kind: IntakeKind; size?: number }): JSX.Element {
  const c = "var(--ink-3)";
  switch (kind) {
    case "photo":   return <ImageIcon size={size} style={{ color: c }} />;
    case "report":  return <FileText size={size} style={{ color: c }} />;
    case "data":    return <Sheet size={size} style={{ color: c }} />;
    case "drawing": return <Ruler size={size} style={{ color: c }} />;
    default:        return <FileIcon size={size} style={{ color: c }} />;
  }
}

// ── Folder row ──
export function FolderRow({ active, dropActive, onClick, onDragOver, onDragLeave, onDrop, icon, label, count, accentCount, depth = 0 }: {
  active: boolean; dropActive?: boolean; onClick: () => void;
  onDragOver?: (e: React.DragEvent) => void; onDragLeave?: () => void; onDrop?: () => void;
  icon: JSX.Element; label: string; count: number; accentCount?: boolean; depth?: number;
}): JSX.Element {
  return (
    <button onClick={onClick} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
            className="w-full flex items-center gap-2 py-1.5 pr-3 text-left transition-colors hover:bg-[rgba(31,78,121,0.05)]"
            style={{ paddingLeft: 14 + depth * 15,
                     background: dropActive ? "rgba(var(--accent-rgb) / 0.20)" : active ? "rgba(var(--accent-rgb) / 0.10)" : undefined,
                     borderLeft: `2px solid ${dropActive || active ? "var(--accent)" : "transparent"}`, outline: dropActive ? "1px dashed var(--accent)" : "none" }}>
      <span className="shrink-0" style={{ color: active || dropActive ? "var(--accent-deep)" : "var(--ink-4)" }}>{icon}</span>
      <span className="font-mono truncate flex-1" style={{ fontSize: 12, color: active ? "var(--ink-1)" : "var(--ink-2)", fontWeight: active ? 600 : 400 }}>{label}</span>
      {count > 0 && <span className="font-mono tabular-nums shrink-0" style={{ fontSize: 10, color: accentCount && count > 0 ? "var(--accent-deep)" : "var(--ink-4)" }}>{count}</span>}
    </button>
  );
}

// ── Collapsible tree header (Plant / Area) ──
export function TreeHeader({ open, onClick, icon, label, count, depth }: {
  open: boolean; onClick: () => void; icon: JSX.Element; label: string; count?: number; depth: number;
}): JSX.Element {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-1.5 py-1.5 pr-3 text-left transition-colors hover:bg-[rgba(31,78,121,0.05)]"
            style={{ paddingLeft: 8 + depth * 15 }}>
      <span className="shrink-0" style={{ color: "var(--ink-4)" }}>{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
      <span className="shrink-0" style={{ color: "var(--ink-4)" }}>{icon}</span>
      <span className="font-mono truncate flex-1" style={{ fontSize: 11.5, color: "var(--ink-2)", fontWeight: 600 }}>{label}</span>
      {count != null && count > 0 && <span className="font-mono tabular-nums shrink-0" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>{count}</span>}
    </button>
  );
}

// ── Checkbox (square, engineering-skinned) ──
export function CheckBox({ on, indeterminate, onClick, title }: { on: boolean; indeterminate?: boolean; onClick: () => void; title?: string }): JSX.Element {
  const filled = on || indeterminate;
  return (
    <button title={title} onClick={(e) => { e.stopPropagation(); onClick(); }}
            className="flex items-center justify-center align-middle transition-colors hover:border-[var(--accent)]"
            style={{ width: 15, height: 15, borderRadius: 3, border: `1px solid ${filled ? "var(--accent)" : "var(--ink-4)"}`,
                     background: filled ? "var(--accent)" : "transparent" }}>
      {on ? <Check size={11} strokeWidth={3} style={{ color: "#fff" }} />
          : indeterminate ? <Minus size={11} strokeWidth={3} style={{ color: "#fff" }} /> : null}
    </button>
  );
}

// ── Sortable header ──
export function Hd({ label, k, sort, setSort, right }: {
  label: string; k: SortKey; sort: { key: SortKey; asc: boolean }; setSort: (s: { key: SortKey; asc: boolean }) => void; right?: boolean;
}): JSX.Element {
  const active = sort.key === k;
  return (
    <th className={`px-3 py-2 font-mono uppercase font-medium select-none cursor-pointer ${right ? "text-right" : "text-left"} ${k === "name" ? "pl-1" : ""}`}
        style={{ fontSize: 9, letterSpacing: ".16em", color: active ? "var(--ink-2)" : "var(--ink-4)" }}
        onClick={() => setSort({ key: k, asc: active ? !sort.asc : true })}>
      <span className={`inline-flex items-center gap-1 ${right ? "flex-row-reverse" : ""}`}>{label}{active && <ArrowUpDown size={9} style={{ transform: sort.asc ? "none" : "scaleY(-1)" }} />}</span>
    </th>
  );
}


export function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="px-3 py-2 border-r last:border-r-0" style={{ borderColor: "var(--hairline)" }}>
      <div className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>{label}</div>
      <div className="font-display font-bold tabular-nums" style={{ fontSize: 20, color: "var(--ink-1)" }}>{value}</div>
    </div>
  );
}
export function PickerNote({ children }: { children: ReactNode }): JSX.Element {
  return <div className="px-4 py-6 font-mono text-center" style={{ fontSize: 11, color: "var(--ink-3)" }}>{children}</div>;
}
export function Center({ children }: { children: ReactNode }): JSX.Element {
  return <div className="h-full flex items-center justify-center font-mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>{children}</div>;
}
